import { useMemo, useState } from 'react';
import {
	Alert, Box, Card, CardContent, Chip, InputAdornment, MenuItem,
	Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ERROR_CATALOG } from '@/data/errorCodes';

const ALL_GROUPS = 'ALL';

function httpChipColor(http: number | undefined): 'default' | 'info' | 'warning' | 'error' {
	if (!http) return 'default';
	if (http >= 500) return 'error';
	if (http >= 400) return 'warning';
	return 'info';
}

export const ErrorCodesReferenceTab = () => {
	const [query, setQuery] = useState('');
	const [groupFilter, setGroupFilter] = useState<string>(ALL_GROUPS);

	const filteredGroups = useMemo(() => {
		const q = query.trim().toLowerCase();
		return ERROR_CATALOG
			.filter((g) => groupFilter === ALL_GROUPS || g.range === groupFilter)
			.map((g) => {
				if (!q) return g;
				const rows = g.rows.filter((r) =>
					r.code.toLowerCase().includes(q)
					|| r.name.toLowerCase().includes(q)
					|| r.description.toLowerCase().includes(q)
					|| (r.http !== undefined && String(r.http).includes(q))
					|| (r.betterAuthSlugs?.some((s) => s.toLowerCase().includes(q)) ?? false)
				);
				return { ...g, rows };
			})
			.filter((g) => g.rows.length > 0);
	}, [query, groupFilter]);

	const totalMatches = useMemo(
		() => filteredGroups.reduce((sum, g) => sum + g.rows.length, 0),
		[filteredGroups]
	);

	return (
		<Stack spacing={2.5}>
			<Alert severity="info" variant="outlined">
				Mã lỗi nội bộ là chuỗi 5 ký tự, hiển thị cho người dùng dưới dạng <code>[E-10001]</code>.
				Backend là nguồn sự thật — frontend chỉ đọc trường <code>code</code> trong response và hiển thị.
				Nhóm <code>30xxx</code> và <code>9xxxx</code> chỉ sinh tại frontend khi không có response từ backend.
			</Alert>

			<Card variant="outlined">
				<CardContent>
					<Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
						<TextField
							size="small"
							placeholder="Tìm theo mã, tên, mô tả, HTTP status, Better Auth slug…"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							sx={{ flexGrow: 1 }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								),
							}}
						/>
						<TextField
							select
							size="small"
							label="Nhóm"
							value={groupFilter}
							onChange={(e) => setGroupFilter(e.target.value)}
							sx={{ minWidth: 220 }}
						>
							<MenuItem value={ALL_GROUPS}>Tất cả nhóm</MenuItem>
							{ERROR_CATALOG.map((g) => (
								<MenuItem key={g.range} value={g.range}>
									{g.range} — {g.name}
								</MenuItem>
							))}
						</TextField>
						<Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
							{totalMatches} mã
						</Typography>
					</Stack>
				</CardContent>
			</Card>

			{filteredGroups.length === 0 && (
				<Alert severity="warning">Không tìm thấy mã lỗi nào khớp với bộ lọc.</Alert>
			)}

			{filteredGroups.map((group) => (
				<Card key={group.range} variant="outlined">
					<CardContent>
						<Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
							<Chip label={group.range} size="small" color="primary" />
							<Typography variant="h6" fontWeight={700}>{group.name}</Typography>
							{group.frontendOnly && (
								<Chip label="Frontend-only" size="small" color="secondary" variant="outlined" />
							)}
						</Stack>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
							{group.description}
						</Typography>

						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 700, width: 90 }}>Mã</TableCell>
										<TableCell sx={{ fontWeight: 700, width: 280 }}>Tên</TableCell>
										<TableCell sx={{ fontWeight: 700, width: 80 }}>HTTP</TableCell>
										<TableCell sx={{ fontWeight: 700 }}>Mô tả</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{group.rows.map((row) => (
										<TableRow key={row.code} hover>
											<TableCell>
												<Box component="code" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
													{row.code}
												</Box>
											</TableCell>
											<TableCell>
												<Box component="code" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
													{row.name}
												</Box>
											</TableCell>
											<TableCell>
												{row.http
													? <Chip label={row.http} size="small" color={httpChipColor(row.http)} variant="outlined" />
													: <Typography variant="caption" color="text.disabled">—</Typography>}
											</TableCell>
											<TableCell>
												<Stack spacing={0.5}>
													<Typography variant="body2">{row.description}</Typography>
													{row.betterAuthSlugs && row.betterAuthSlugs.length > 0 && (
														<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
															{row.betterAuthSlugs.map((slug) => (
																<Chip
																	key={slug}
																	label={slug}
																	size="small"
																	variant="outlined"
																	sx={{ fontFamily: 'monospace', fontSize: '0.7em', height: 20 }}
																/>
															))}
														</Stack>
													)}
												</Stack>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</CardContent>
				</Card>
			))}
		</Stack>
	);
};
